// src/pages/tenant/TenantSwitcherPage.tsx
// Grid of tenant cards the current user belongs to, with ability to switch active tenant.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map tenant type enum to human-readable label. */
function typeLabel(type: string): string {
  switch (type) {
    case 'TENANT_TYPE_PERSONAL':
      return 'Personal';
    case 'TENANT_TYPE_TEAM':
      return 'Team';
    case 'TENANT_TYPE_ORGANIZATION':
      return 'Organization';
    case 'TENANT_TYPE_ENTERPRISE':
      return 'Enterprise';
    default:
      return type?.replace('TENANT_TYPE_', '') || 'Unknown';
  }
}

/** Map tenant type enum to badge variant. */
function typeVariant(type: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' {
  switch (type) {
    case 'TENANT_TYPE_PERSONAL':
      return 'secondary';
    case 'TENANT_TYPE_TEAM':
      return 'default';
    case 'TENANT_TYPE_ORGANIZATION':
      return 'primary';
    case 'TENANT_TYPE_ENTERPRISE':
      return 'warning';
    default:
      return 'secondary';
  }
}

/** Map tenant status to dot color class. */
function statusDotColor(status: string): string {
  switch (status) {
    case 'TENANT_STATUS_ACTIVE':
      return 'bg-green-500';
    case 'TENANT_STATUS_SUSPENDED':
      return 'bg-red-500';
    case 'TENANT_STATUS_PENDING':
      return 'bg-yellow-500';
    case 'TENANT_STATUS_ARCHIVED':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

/** Map tenant status to human-readable label. */
function statusLabel(status: string, t: (k: string) => string): string {
  switch (status) {
    case 'TENANT_STATUS_ACTIVE':
      return t('tenant.status.active');
    case 'TENANT_STATUS_SUSPENDED':
      return t('tenant.status.suspended');
    case 'TENANT_STATUS_PENDING':
      return t('tenant.status.pending');
    case 'TENANT_STATUS_ARCHIVED':
      return t('tenant.status.archived');
    default:
      return status?.replace('TENANT_STATUS_', '') || 'Unknown';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantSwitcherPage - grid of tenant cards the current user belongs to.
 * Route: /tenants/switch
 */
export const TenantSwitcherPage = observer(() => {
  const store = useTenantTenant();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<any[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);

  useEffect(() => {
    store.ListTenants({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
    }).then(() => {
      setTenants(store.items ?? []);
    }).catch(() => {
      setTenants(store.items ?? []);
    });

    // Determine current active tenant
    const current = (store as any).activeTenant ?? (store as any).currentTenant;
    if (current) {
      setActiveTenantId(current.entity?.id || current.id || null);
    }
  }, []);

  const handleSwitchTenant = async (tenant: any) => {
    const tenantId = tenant.entity?.id || tenant.id;
    if (!tenantId) return;

    try {
      // Tenant switching is intentionally client-side: there is no
      // SetActiveTenant/SwitchTenant RPC by design — the active tenant is a
      // client concern, so we just record the selection and navigate.
      setActiveTenantId(tenantId);
      navigate('/dashboard');
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('tenant.switcher.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('tenant.switcher.subtitle')}
          </p>
        </div>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Loading */}
      {store.isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )}

      {/* Empty state */}
      {!store.isLoading && tenants.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {t('tenant.switcher.empty')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              {t('tenant.switcher.emptyDescription')}
            </p>
            <Link to="/tenants/new">
              <Button>{t('tenant.create')}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Tenant card grid */}
      {!store.isLoading && tenants.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {tenants.map((tenant: any) => {
            const tenantId = tenant.entity?.id || tenant.id;
            const isActive = activeTenantId === tenantId;
            const name = tenant.name ?? '';
            const slug = tenant.slug ?? '';
            const description = tenant.description ?? '';
            const type = tenant.type ?? '';
            const status = tenant.status ?? 'TENANT_STATUS_ACTIVE';
            const memberCount = tenant.member_count ?? tenant.members_count ?? 0;
            const region = tenant.region ?? '';
            const initial = (name || slug || '?')[0]?.toUpperCase() ?? '?';

            return (
              <Card
                key={tenantId}
                className={`flex flex-col cursor-pointer transition-all hover:shadow-md ${
                  isActive ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => handleSwitchTenant(tenant)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    {/* Tenant avatar */}
                    {tenant.logo_url ? (
                      <img
                        src={tenant.logo_url}
                        alt={name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-primary">{initial}</span>
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base truncate">{name}</CardTitle>
                        {isActive && (
                          <Badge variant="success" size="sm">
                            {t('tenant.switcher.current')}
                          </Badge>
                        )}
                      </div>
                      {slug && (
                        <CardDescription className="text-xs truncate mt-0.5">
                          {slug}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 space-y-3 pb-3">
                  {/* Description */}
                  {description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {description}
                    </p>
                  )}

                  {/* Type and status row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {type && (
                      <Badge variant={typeVariant(type)} size="sm">
                        {typeLabel(type)}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${statusDotColor(status)}`} />
                      <span className="text-xs text-muted-foreground">
                        {statusLabel(status, t)}
                      </span>
                    </div>
                  </div>

                  {/* Metadata row */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {memberCount > 0 && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        </svg>
                        <span>
                          {memberCount} {memberCount === 1
                            ? t('tenant.switcher.member')
                            : t('tenant.switcher.members')}
                        </span>
                      </div>
                    )}
                    {region && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                        </svg>
                        <span>{region}</span>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="pt-3 border-t border-border">
                  <Button
                    variant={isActive ? 'secondary' : 'primary'}
                    size="sm"
                    className="w-full"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleSwitchTenant(tenant);
                    }}
                  >
                    {isActive
                      ? t('tenant.switcher.currentWorkspace')
                      : t('tenant.switcher.switch')}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}

          {/* Create New Tenant card */}
          <Link to="/tenants/new" className="block">
            <Card className="flex flex-col h-full border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer group">
              <CardContent className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center mb-4 transition-colors">
                  <svg
                    className="w-7 h-7 text-muted-foreground group-hover:text-primary transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                  {t('tenant.switcher.createNew')}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('tenant.switcher.createNewDescription')}
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}
    </div>
  );
});

export default TenantSwitcherPage;
